import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, ChevronDown } from 'lucide-react'

interface NamespaceEducationProps {
  onNext: () => void
}

/**
 * NamespaceEducation - Minimal namespace explanation
 *
 * "Innovation distinguishes between a leader and a follower." — Steve Jobs
 */

const NAMESPACES = [
  {
    id: 'core',
    title: 'Core',
    brief: 'Legal identity',
    description: 'Verified name, legal identifiers. Only accessed when compliance demands.',
  },
  {
    id: 'behavioral',
    title: 'Behavioral',
    brief: 'Your patterns',
    description: 'How you engage — reasoning modes, communication styles, preferences.',
  },
  {
    id: 'relational',
    title: 'Relational',
    brief: 'Trust networks',
    description: 'Who you collaborate with, which sources you trust. You control visibility.',
  },
  {
    id: 'contextual',
    title: 'Contextual',
    brief: 'Domain-specific',
    description: 'Medical stays in healthcare. Financial remains with banks. No bleeding.',
  },
  {
    id: 'preference',
    title: 'Preference',
    brief: 'Your settings',
    description: 'Configuration, accessibility, customizations. These follow you everywhere.',
  },
  {
    id: 'temporal',
    title: 'Temporal',
    brief: 'Your evolution',
    description: 'How interests shift, expertise develops. Identity that grows with you.',
  },
]

export default function NamespaceEducation({ onNext }: NamespaceEducationProps) {
  const [expanded, setExpanded] = useState<string | null>(null)
  const [viewedNamespaces, setViewedNamespaces] = useState<Set<string>>(new Set())

  const handleToggle = (id: string) => {
    setExpanded(expanded === id ? null : id)

    if (!viewedNamespaces.has(id)) {
      setViewedNamespaces(new Set([...viewedNamespaces, id]))
    }
  }

  const hasExploredEnough = viewedNamespaces.size >= 3

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-8"
    >
      {/* Header */}
      <div className="space-y-3">
        <h2 className="text-3xl font-extralight tracking-tight text-white">
          Six dimensions
        </h2>
        <p className="text-white/40 font-light">
          Your identity, organized and protected.
        </p>
      </div>

      {/* Privacy note */}
      <p className="text-xs text-white/30 font-light">
        Each namespace is isolated. Apps only access what you grant.
      </p>

      {/* Namespace list - accordion style */}
      <div className="space-y-1">
        {NAMESPACES.map((ns, index) => (
          <motion.div
            key={ns.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.05 }}
          >
            <button
              onClick={() => handleToggle(ns.id)}
              className="w-full flex items-center justify-between py-4 border-b border-white/10
                       hover:border-white/20 transition-colors duration-300 text-left"
            >
              <div className="flex items-center gap-4">
                <span className="text-xs text-white/20 font-mono w-4">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div>
                  <span className="text-white/80 font-light">{ns.title}</span>
                  <span className="text-white/30 text-sm ml-3">{ns.brief}</span>
                </div>
              </div>
              <motion.div
                animate={{ rotate: expanded === ns.id ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="w-4 h-4 text-white/30" />
              </motion.div>
            </button>

            <AnimatePresence>
              {expanded === ns.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <p className="py-4 pl-8 text-sm text-white/40 font-light leading-relaxed">
                    {ns.description}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      {/* Progress */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-white/20 uppercase tracking-widest">
          {viewedNamespaces.size}/6 explored
        </span>
        <div className="flex gap-1">
          {NAMESPACES.map((ns) => (
            <div
              key={`dot-${ns.id}`}
              className={`
                w-1 h-1 transition-all duration-300
                ${viewedNamespaces.has(ns.id) ? 'bg-white' : 'bg-white/10'}
              `}
            />
          ))}
        </div>
      </div>

      {/* Continue button */}
      <button
        onClick={onNext}
        className={`
          w-full py-4 text-sm font-medium tracking-wide
          flex items-center justify-center gap-3
          transition-all duration-200
          ${hasExploredEnough
            ? 'bg-white text-black hover:bg-white/90'
            : 'bg-transparent border border-white/20 text-white/50'
          }
        `}
      >
        {hasExploredEnough ? (
          <>
            <span>Continue</span>
            <ArrowRight className="w-4 h-4" />
          </>
        ) : (
          <span>Explore {3 - viewedNamespaces.size} more</span>
        )}
      </button>

      {/* Skip option */}
      {!hasExploredEnough && (
        <button
          onClick={onNext}
          className="w-full text-center text-[10px] text-white/20 hover:text-white/40
                   transition-colors duration-300"
        >
          Skip
        </button>
      )}
    </motion.div>
  )
}
